import { FailedMessageWithHref } from 'common/ui';
import { Box, Typography } from '@mui/material';
import { Header } from 'common/ui/Header';
import { getTemplateEditBlockedReason } from 'modules/calendar/utils/getTemplateEditBlockedReason';
import { toEditableOrganizer } from 'modules/calendar/utils/toEditableOrganizer';
import {
  apiUpdateCalendarMeetingTemplateSchema,
  useUpdateCalendarMeetingTemplate
} from 'modules/calendar/api/useUpdateMeetingTemplate';
import { useFetchCalendarMeetingTemplate } from 'modules/calendar/api/useFetchCalendarMeetingTemplate';
import { UpsertCalendarMeetingTemplateForm } from 'modules/calendar/components/UpsertCalendarMeetingTemplateForm';

import { CalendarMeetingTemplateFormData } from 'modules/calendar/constants/calendarMeetingTemplateSchema';
import { useUserCalendar } from 'modules/calendar/hooks/useUserCalendar';
import { mapCreateCalendarMeetingTemplateSchema } from 'modules/calendar/utils/mapCreateCalendarMeetingTemplateSchema';
import { resolveEditAvailabilityMethod } from 'modules/calendar/utils/resolveEditAvailabilityMethod';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLoadingOverlay } from 'services/loading/hooks';
import { Paths } from 'services/navigation/paths';
import { ToastType, useToast } from 'services/toast';
import { useQueryClient } from '@tanstack/react-query';
import { ApiPaths } from 'modules/calendar/constants/apiPaths';
import { mapFormDataToMeetingTemplate } from 'modules/calendar/utils/mapFormDataToMeetingTemplate';

type Props = {
  isPreview?: boolean;
};

export const CalendarEditMeetingTemplatePage: React.FC<Props> = ({
  isPreview
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showLoadingOverlay, hideLoadingOverlay } = useLoadingOverlay();
  const { id } = useParams();
  // An update replaces the whole panel, so anything unresolved here is deleted.
  const { data: userCalendars } = useUserCalendar({});
  const { mutateAsync: updateCalendarMeetingTemplate } =
    useUpdateCalendarMeetingTemplate();

  const {
    data: formData,
    isLoading: isFetchingFormData,
    isError,
    unresolvedParticipants,
    hasStoredOutreachSettings
  } = useFetchCalendarMeetingTemplate({
    id,
    userCalendars
  });

  const organizerCalendar = formData?.owner
    ? userCalendars.find((calendar) => calendar.id === formData.owner)
    : undefined;

  const unresolvedOrganizer = unresolvedParticipants.find(
    (participant) => participant.isOrganizer
  );

  // Blocking: the organizer can't be changed once a template exists.
  const readOnlyReason = getTemplateEditBlockedReason({
    availabilityMethod: formData?.schedulingMethod,
    organizerUserCalendar: toEditableOrganizer({
      calendar: organizerCalendar,
      unresolved: unresolvedOrganizer
    })
  });

  // Warning only: deleted on save, but the template still works without them.
  const unresolvedMembers = unresolvedParticipants.filter(
    (participant) => !participant.isOrganizer
  );

  useEffect(() => {
    if (isFetchingFormData) {
      showLoadingOverlay();
    } else {
      hideLoadingOverlay();
    }
  }, [isFetchingFormData]);

  const { showToast } = useToast();

  const handleSave = async (
    data: CalendarMeetingTemplateFormData,
    dirtyFields: string[] = []
  ) => {
    if (formData && data.owner !== formData.owner) {
      showToast({
        title:
          "We don't currently support changing the meeting organiser once published",
        message:
          "If you'd like to invite candidates with a different organiser, create a new meeting.",
        type: ToastType.Error
      });
      return;
    }

    try {
      showLoadingOverlay();

      // Seeded for display, so a value alone isn't reason to write it.
      const omitOutreachSettings =
        !hasStoredOutreachSettings && !dirtyFields.includes('requiredHours');

      const apiSchema = await mapCreateCalendarMeetingTemplateSchema(
        data,
        userCalendars,
        {
          methodOverride: resolveEditAvailabilityMethod(
            formData?.schedulingMethod,
            data.schedulingMethod
          ),
          omitOutreachSettings
        }
      );

      // usePatchRest is a bare passthrough, so the schema never runs itself.
      const body = await apiUpdateCalendarMeetingTemplateSchema.validate(
        { templateId: id, ...apiSchema },
        { abortEarly: false }
      );

      const { data: updatedData } = await updateCalendarMeetingTemplate(body);

      await queryClient.invalidateQueries({
        queryKey: [`${ApiPaths.GET_CALENDAR_MEETING_TEMPLATE}/${id}`]
      });

      showToast({
        title: 'Meeting template edited',
        type: ToastType.Success
      });

      navigate(Paths.HOME_CALENDAR_MEETINGS, {
        state: {
          calendarMeetingTemplate: mapFormDataToMeetingTemplate({
            formData: data,
            id: updatedData.id,
            userCalendars: userCalendars
          })
        }
      });
    } catch (error) {
      showToast({
        title: 'Something went wrong',
        message: (
          <FailedMessageWithHref
            failedMessage="Try editing this meeting template again."
            emailSubject="Failed to edit meeting template"
          />
        ),
        type: ToastType.Error
      });
    } finally {
      hideLoadingOverlay();
    }
  };

  if (isFetchingFormData || !userCalendars || userCalendars.length === 0) {
    return null;
  }

  if (isError) {
    return (
      <Box sx={{ pt: 0, maxWidth: 800 }}>
        <Header
          breadcrumbs={[
            { name: 'Meetings', path: Paths.HOME_CALENDAR_MEETINGS }
          ]}
          currentBreadcrumb="Meeting template"
          title="This template can't be opened"
          onBack={() => navigate(Paths.HOME_CALENDAR_MEETINGS)}
        />
        <Typography variant="body1" sx={{ mt: 3 }}>
          Its scheduling configuration is missing, so there is nothing to show.
          Create a new template instead.
        </Typography>
      </Box>
    );
  }

  if (!formData) {
    return null;
  }

  return (
    <UpsertCalendarMeetingTemplateForm
      type={isPreview ? 'preview' : 'edit'}
      breadcrumbs={[{ name: 'Meetings', path: Paths.HOME_CALENDAR_MEETINGS }]}
      currentBreadcrumb={
        isPreview ? 'Meeting template' : 'Edit Meeting Template'
      }
      title={isPreview ? 'Meeting template' : 'Edit meeting template'}
      defaultFormData={formData}
      userCalendars={userCalendars}
      readOnlyReason={readOnlyReason}
      unresolvedMembers={unresolvedMembers}
      onSave={handleSave}
    />
  );
};
